import React, { FC } from 'react';

import { CollectionProgressItem } from 'api-client';
import { DataTable, Money, StatusBadge } from 'components/common';
import { formatPercents } from 'formatters';

import {
  formatCollectionDate,
  getCollectionStatusLabel,
  getCollectionStatusTone,
  getPaymentStatusLabel,
  getPaymentStatusTone,
} from './collectionVariants';
import styles from './ProjectCollection.module.scss';

/**
 * Один проект взыскания: сводка показателей и график платежей.
 *
 * Заголовок — нейтральный порядковый номер ("Взыскание 1"), а не идентификатор реестра:
 * тот содержит названия должника и клиента и наружу не отдаётся. Показывается только
 * когда у проекта больше одного проекта взыскания.
 */
export const CollectionSection: FC<{ item: CollectionProgressItem; showHeading: boolean }> = ({
  item,
  showHeading,
}) => {
  // targetAchievementPercent — это ДОЛЯ (1.1775 = 117,75 %), умножение на 100 только здесь.
  const achievement =
    item.targetAchievementPercent === undefined
      ? '—'
      : formatPercents(item.targetAchievementPercent * 100, 1, true);

  return (
    <div className={styles.section}>
      {showHeading && <h3 className={styles.sectionHeading}>Взыскание {item.sequence}</h3>}

      <DataTable columns="repeat(2, minmax(0, 1fr))" variant="surface" className={styles.summary}>
        <DataTable.Body>
          <DataTable.Row>
            <DataTable.Cell>Статус</DataTable.Cell>
            <DataTable.Cell align="right">
              <StatusBadge tone={getCollectionStatusTone(item.status)}>
                {getCollectionStatusLabel(item.status)}
              </StatusBadge>
            </DataTable.Cell>
          </DataTable.Row>
          <DataTable.Row>
            <DataTable.Cell>Целевая сумма взыскания</DataTable.Cell>
            <DataTable.Cell align="right">
              {item.targetSum ? <Money money={item.targetSum} /> : '—'}
            </DataTable.Cell>
          </DataTable.Row>
          <DataTable.Row>
            {/* Намеренно не просто "Взыскано": в экономическом анализе есть своё
                "Фактическое взыскание", заполняемое вручную, и два числа не должны
                читаться как один и тот же показатель. */}
            <DataTable.Cell>Поступило по проекту взыскания</DataTable.Cell>
            <DataTable.Cell align="right">
              {item.paidSum ? <Money money={item.paidSum} /> : '—'}
            </DataTable.Cell>
          </DataTable.Row>
          <DataTable.Row>
            <DataTable.Cell>Остаток ко взысканию</DataTable.Cell>
            <DataTable.Cell align="right">
              {item.plannedSum ? <Money money={item.plannedSum} /> : '—'}
            </DataTable.Cell>
          </DataTable.Row>
          <DataTable.Row>
            <DataTable.Cell>Достижение целевой суммы</DataTable.Cell>
            <DataTable.Cell align="right">{achievement}</DataTable.Cell>
          </DataTable.Row>
          <DataTable.Row>
            <DataTable.Cell>Категория урегулирования</DataTable.Cell>
            <DataTable.Cell align="right">{item.strategyCategory ?? '—'}</DataTable.Cell>
          </DataTable.Row>
          <DataTable.Row>
            <DataTable.Cell>Дата начала работы</DataTable.Cell>
            <DataTable.Cell align="right">{formatCollectionDate(item.addedAt)}</DataTable.Cell>
          </DataTable.Row>
          <DataTable.Row>
            <DataTable.Cell>Плановая дата взыскания</DataTable.Cell>
            <DataTable.Cell align="right">
              {formatCollectionDate(item.finishPlannedAt)}
            </DataTable.Cell>
          </DataTable.Row>
          {item.publicClosedAt && (
            <DataTable.Row>
              <DataTable.Cell>Проект закрыт для инвестора</DataTable.Cell>
              <DataTable.Cell align="right">
                {formatCollectionDate(item.publicClosedAt)}
              </DataTable.Cell>
            </DataTable.Row>
          )}
          {item.collectionDays !== undefined && (
            <DataTable.Row>
              <DataTable.Cell>Время взыскания</DataTable.Cell>
              <DataTable.Cell align="right">{item.collectionDays}&nbsp;дн.</DataTable.Cell>
            </DataTable.Row>
          )}
        </DataTable.Body>
      </DataTable>

      <h4 className={styles.paymentsHeading}>График платежей</h4>
      <DataTable columns="repeat(3, minmax(0, 1fr))" variant="surface">
        <DataTable.Head>
          <DataTable.HeadCell>Дата</DataTable.HeadCell>
          <DataTable.HeadCell>Статус</DataTable.HeadCell>
          <DataTable.HeadCell align="right">Сумма</DataTable.HeadCell>
        </DataTable.Head>
        <DataTable.Body>
          {item.payments.length === 0 ? (
            <DataTable.Empty>Платежей по этому проекту взыскания пока нет</DataTable.Empty>
          ) : (
            item.payments.map((payment) => (
              <DataTable.Row key={payment.uuid}>
                <DataTable.Cell>{formatCollectionDate(payment.paymentDate)}</DataTable.Cell>
                <DataTable.Cell>
                  <StatusBadge tone={getPaymentStatusTone(payment.status)}>
                    {getPaymentStatusLabel(payment.status)}
                  </StatusBadge>
                </DataTable.Cell>
                <DataTable.Cell align="right">
                  {payment.totalSum ? <Money money={payment.totalSum} /> : '—'}
                </DataTable.Cell>
              </DataTable.Row>
            ))
          )}
        </DataTable.Body>
      </DataTable>
    </div>
  );
};

export default CollectionSection;
